import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { Spinner } from "reactstrap";
import BaseModal from "Components/Base/BaseModal";
import BaseInput from "Components/Base/BaseInput";
import { BaseSelect } from "Components/Base/BaseSelect";
import { createService, calculateServiceGstApi } from "Api/service";
import { hsnDropdownApi } from "Api/Quotation";
import {
  checkStatusCodeSuccess,
  Loading,
  TaxType,
} from "Components/constants/Common";
import {
  addServiceLabel,
  ServiceName,
  Cost,
  Description,
  GstAmount,
  WithGstAmount,
} from "Components/constants/Services";
import {
  InputPlaceHolder,
  SelectPlaceHolder,
  validationMessages,
} from "Components/constants/validation";
import { cancelLabel, submitLabel } from "Components/constants/Role";
import { HSNSACLabel } from "Components/constants/HsnSac";
import { GST_LABEL } from "Components/constants/ROModule";

interface ServiceModalProps {
  open: boolean;
  toggle: () => void;
  onSuccess: (selectedService?: any) => void;
}

const ServiceModal: React.FC<ServiceModalProps> = ({
  open,
  toggle,
  onSuccess,
}) => {
  const [loader, setLoader] = useState<boolean>(false);
  const [submitLoader, setSubmitLoader] = useState<boolean>(false);
  const [hsnSacOption, setHsnSacOption] = useState<Array<any>>([]);

  const fetchHsnSac = () => {
    setLoader(true);
    hsnDropdownApi()
      .then((res) => {
        if (checkStatusCodeSuccess(res?.statusCode)) {
          const data = res?.data?.map((item: any) => ({
            value: item?.id,
            label: item?.code,
            type: item?.type,
            id: item?.id,
          }));
          setHsnSacOption(data);
        } else {
          setHsnSacOption([]);
        }
      })
      .catch((err) => {
        setHsnSacOption([]);
      })
      .finally(() => {
        setLoader(false);
      });
  };

  useEffect(() => {
    if (open) {
      fetchHsnSac();
    }
  }, [open]);

  const formik = useFormik({
    initialValues: {
      service_name: "",
      description: "",
      hsnSac: null,
      gst: null,
      cost: null,
      gstAmount: null,
      withGstAmount: null,
    },
    validationSchema: yup.object({
      service_name: yup
        .string()
        .required(validationMessages.required(ServiceName)),
      description: yup
        .string()
        .required(validationMessages.required(Description)),
      hsnSac: yup.string().required(validationMessages.required(HSNSACLabel)),
      gst: yup.string().required(validationMessages.required(GST_LABEL)),
      cost: yup
        .number()
        .required(validationMessages.required(Cost))
        .positive()
        .integer(),
      gstAmount: yup.string().required(validationMessages.required(GstAmount)),
      withGstAmount: yup
        .string()
        .required(validationMessages.required(WithGstAmount)),
    }),
    onSubmit: async (values) => {
      setSubmitLoader(true);
      const payload = {
        service_name: values.service_name,
        cost: values.cost,
        hsn_sac_id: values.hsnSac,
        gst: values.gst,
        description: values.description,
        gst_amount: values.gstAmount,
        with_gst_amount: values.withGstAmount,
      };

      createService(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            onSuccess(res?.data);
            toggle();
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message || error?.message);
        })
        .finally(() => setSubmitLoader(false));
    },
  });

  const calculateGst = () => {
    const payload = {
      rate: formik.values.cost,
      gst: formik.values.gst,
    };
    const isValidPayload = payload.gst !== null && payload.rate !== null;
    if (isValidPayload) {
      calculateServiceGstApi(payload)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            formik.setFieldValue("gstAmount", res?.data?.gstAmount);
            formik.setFieldValue("withGstAmount", res?.data?.totalGstAmount);
          }
        })
        .catch((err) => {
          setLoader(false);
        })
        .finally(() => {
          setLoader(false);
        });
    }
  };

  useEffect(() => {
    calculateGst();
  }, [formik.values.cost, formik.values.gst]);

  return (
    <BaseModal
      open={open}
      toggle={toggle}
      title={addServiceLabel}
      submit={formik.handleSubmit}
      closeLabel={cancelLabel}
      submitLabel={submitLabel}
      closeLabelColor="danger"
      submitLabelColor="success"
      loader={submitLoader}
    >
      {loader ? (
        <div className="d-flex justify-content-center align-items-center">
          <Spinner>{Loading}</Spinner>
        </div>
      ) : (
        <div className="form-group">
          <div className="row">
            <div className="col-12 col-md-6">
              <BaseInput
                type="text"
                label={ServiceName}
                placeholder={InputPlaceHolder(ServiceName)}
                name="service_name"
                className="form-control"
                value={formik.values.service_name}
                onChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                touched={formik.touched.service_name}
                error={formik.errors.service_name}
                required
              />
            </div>
            <div className="col-12 col-md-6">
              <BaseInput
                type="textarea"
                label={Description}
                placeholder={InputPlaceHolder(Description)}
                name="description"
                className="form-control"
                value={formik.values.description}
                onChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                touched={formik.touched.description}
                error={formik.errors.description}
                required
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-6">
              <BaseSelect
                name="hsnSac"
                label={HSNSACLabel}
                options={hsnSacOption}
                placeholder={SelectPlaceHolder(HSNSACLabel)}
                value={formik.values.hsnSac}
                handleChange={(field: any, value: any) => {
                  formik.setFieldValue(field, value);
                }}
                handleBlur={formik.handleBlur}
                error={formik.errors.hsnSac}
                touched={formik.touched.hsnSac}
                required
              />
            </div>
            <div className="col-12 col-md-6">
              <BaseInput
                type="number"
                label={Cost}
                placeholder={InputPlaceHolder(Cost)}
                name="cost"
                className="form-control"
                value={formik.values.cost}
                onChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                touched={formik.touched.cost}
                error={formik.errors.cost}
                required
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-6">
              <BaseSelect
                name="gst"
                label={GST_LABEL}
                options={TaxType || []}
                value={formik.values.gst}
                placeholder={SelectPlaceHolder(GST_LABEL)}
                handleChange={(field: any, value: any) => {
                  formik.setFieldValue(field, value);
                }}
                handleBlur={formik.handleBlur}
                error={formik.errors.gst}
                touched={formik.touched.gst}
                required
              />
            </div>
            <div className="col-12 col-md-6">
              <BaseInput
                type="number"
                label={GstAmount}
                placeholder={InputPlaceHolder(GstAmount)}
                name="gstAmount"
                className="form-control"
                value={formik.values.gstAmount}
                onChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                touched={formik.touched.gstAmount}
                error={formik.errors.gstAmount}
                required
                disabled
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-6">
              <BaseInput
                type="number"
                label={WithGstAmount}
                placeholder={InputPlaceHolder(WithGstAmount)}
                name="withGstAmount"
                className="form-control"
                value={formik.values.withGstAmount}
                onChange={formik.handleChange}
                handleBlur={formik.handleBlur}
                touched={formik.touched.withGstAmount}
                error={formik.errors.withGstAmount}
                required
                disabled
              />
            </div>
          </div>
        </div>
      )}
    </BaseModal>
  );
};

export default ServiceModal;
